/**
 * A note in the shared `ActivityCard` shell — a deal's notes, and the customer, location and related notes the
 * record surfaces show (`EntityNoteItem`): headline = the note's own
 * first line, who = the author (or the Pipedrive/Kory badge when the note
 * isn't a person's own words), preview = its second line, time of day, note
 * accent tile (mock §2 "Note and finished to-do"). Same shape and the same
 * Pipedrive/Kory badges `LeadNoteItem` (the LCC's own note thread) carries —
 * this is the deal timeline's card for the same note row, not a fork of its
 * data.
 *
 * The delete mutation itself stays with the caller (`deal-timeline.tsx`):
 * the LCC is going away, and new code doesn't import from
 * `components/lead-command-center/` (conformance-pinned), so this file takes
 * a structural `DealNoteView` instead of the LCC's own `LeadNote` type and a
 * resolved `onDelete` action instead of calling `useDeleteLeadNote` itself.
 */
import { useState, type ReactNode } from "react";
import { parseISO } from "date-fns";
import { Link2, StickyNote, Trash2 } from "lucide-react";
import { KoryIcon } from "@/components/kory/kory-icon";
import { Badge } from "@/components/ui/badge";
import { MentionText } from "@/components/mentions/mention-text";
import { formatBusinessDateTime, formatBusinessTime } from "@/lib/business-date";

import { ActivityCard, type BelongsHereAction } from "./activity-row";

/** The fields this card reads off a note — structurally compatible with the LCC's own
 *  `LeadNote`, so a caller holding one can pass it straight through. */
export interface DealNoteView {
  id: string;
  author_id: string;
  author_name: string | null;
  body: string;
  created_at: string;
  source_system: string | null;
  mentions?: { user_id: string; display_name: string }[];
}

/** The headline sentence, and whatever's left as the preview — read verbatim, never reworded. */
function splitFirstSentence(text: string): { first: string; rest: string | null } {
  const match = text.match(/^(.*?[.!?])\s+([\s\S]*)$/);
  if (!match) return { first: text, rest: null };
  return { first: match[1], rest: match[2] || null };
}

export function NoteCard({
  note,
  canDelete,
  onDelete,
  tag,
  deleteTestId = "lead-note-delete",
  showDate = false,
  unknownAuthor = "Unknown",
}: {
  note: DealNoteView;
  canDelete: boolean;
  /** Present only when `canDelete` — same shape every other card's delete/move action takes. */
  onDelete?: BelongsHereAction;
  /** A tag ahead of the author: where a note from another record is attached ("On 1 Main St"). */
  tag?: ReactNode;
  deleteTestId?: string;
  /** A list with no day headers (the Location page's feed) shows the date with the time. */
  showDate?: boolean;
  /** What a note with no author says; null says nothing (an imported or system customer note). */
  unknownAuthor?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const isPipedrive = note.source_system === "pipedrive";
  // The daily deal plan's analysis note (`sop.hickory_deal.daily_plan`): Kory's leaf is the
  // attribution, so the author line is left to the badge, same rule `LeadNoteItem` uses.
  const isKory = note.source_system === "kory_daily_plan";
  const displayAuthor = isPipedrive
    ? note.author_name?.replace(/^Pipedrive(?: · )?/, "").trim() || null
    : isKory
      ? null
      : (note.author_name ?? unknownAuthor);

  const { first, rest } = splitFirstSentence(note.body);
  const when = parseISO(note.created_at);
  return (
    <ActivityCard
      icon={StickyNote}
      kind="note"
      title={<MentionText text={first} names={(note.mentions ?? []).map((m) => m.display_name)} />}
      who={
        <span className="inline-flex flex-wrap items-center gap-1">
          {tag}
          {isPipedrive ? (
            <Badge
              variant="secondary"
              data-testid="lead-note-pipedrive-badge"
              className="rounded-full bg-blue-50 px-1.5 py-0 text-[10px] font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300"
            >
              <Link2 className="mr-0.5 h-2.5 w-2.5" />
              Pipedrive
            </Badge>
          ) : null}
          {isKory ? (
            <Badge
              variant="secondary"
              data-testid="lead-note-kory-badge"
              title="Written by Kory"
              className="rounded-full px-1.5 py-0 text-[10px] font-medium"
            >
              <KoryIcon alt="" className="mr-0.5 h-2.5 w-2.5" />
              Kory
            </Badge>
          ) : null}
          {displayAuthor}
        </span>
      }
      time={showDate ? formatBusinessDateTime(when) : formatBusinessTime(when)}
      timeTitle={formatBusinessDateTime(when)}
      preview={rest ? <MentionText text={rest} names={(note.mentions ?? []).map((m) => m.display_name)} /> : null}
      actions={
        canDelete && onDelete ? (
          <button
            type="button"
            data-testid={deleteTestId}
            aria-label="Delete note"
            className="-my-0.5 inline-flex size-5 items-center justify-center rounded text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:-my-2 pointer-coarse:size-9"
            disabled={onDelete.pending}
            onClick={(e) => {
              e.stopPropagation();
              onDelete.onClick();
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        ) : null
      }
      // Opening unclamps the headline and the preview (the rest of the note): together they are the full
      // note, so the opened card says nothing twice. Every note opens, since whether a headline clamps
      // depends on the width it renders at.
      open={open}
      onOpenChange={setOpen}
    />
  );
}
